import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  School,
  Mail,
  Phone,
  MapPin,
  User,
  Upload,
  AlertCircle,
  Building,
  Calendar,
  Users,
  Globe,
  Lock,
  Check,
  X,
  ChevronRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import { schoolRegistrationApi } from '../../services/api';
import { SchoolRegistrationFormData } from '../../types';

const inputClass =
  'w-full bg-[#17140f] border border-[#3a3122] rounded-[11px] py-2.5 px-3.5 text-sm text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:border-[#e2916d] focus:ring-2 focus:ring-[#332217] transition-colors';
const inputWithIconClass = `${inputClass} pl-10`;
const labelClass = 'block text-xs font-semibold text-[#b9ad9b] mb-1.5';

const steps = [
  { title: 'School Information', subtitle: 'Name, address & location' },
  { title: 'Admin Details', subtitle: 'Login credentials & principal info' },
  { title: 'Final Details', subtitle: 'Board, type & school logo' }
];

const SchoolRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState<SchoolRegistrationFormData>({
    schoolName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    adminEmail: '',
    adminMobile: '',
    adminPassword: '',
    confirmPassword: '',
    principalName: '',
    principalEmail: '',
    principalMobile: '',
    schoolType: '',
    establishedYear: '',
    totalStudents: '',
    website: '',
    affiliation: '',
    schoolLogo: null,
  });

  const [logoPreview, setLogoPreview] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size should be less than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Please upload a valid image file');
        return;
      }

      setFormData(prev => ({ ...prev, schoolLogo: file }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.schoolName && formData.address && formData.city && formData.state && formData.pincode);
      case 2:
        return !!(formData.adminEmail && formData.adminMobile && formData.adminPassword && formData.confirmPassword && formData.principalName);
      case 3:
        return !!(formData.schoolType && formData.establishedYear && formData.affiliation);
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setError('');
    } else {
      setError('Please fill all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await schoolRegistrationApi.register(formData);

      toast.success('School registration submitted successfully! We will review your application and get back to you within 2-3 business days.');
      navigate('/login');
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || 'Registration failed. Please try again.');
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-[18px]">
      <div>
        <label className={labelClass}>School Name <span className="text-[#e2916d]">*</span></label>
        <div className="relative">
          <School className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
          <input
            type="text"
            name="schoolName"
            value={formData.schoolName}
            onChange={handleInputChange}
            className={inputWithIconClass}
            placeholder="Enter school name"
            required
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>School Address <span className="text-[#e2916d]">*</span></label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3 w-4 h-4 text-[#8a7f6e]" />
          <textarea
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            rows={2}
            className={`${inputWithIconClass} resize-y`}
            placeholder="Enter complete address"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        <div>
          <label className={labelClass}>City <span className="text-[#e2916d]">*</span></label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            className={inputClass}
            placeholder="City"
            required
          />
        </div>

        <div>
          <label className={labelClass}>State <span className="text-[#e2916d]">*</span></label>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            className={inputClass}
            placeholder="State"
            required
          />
        </div>

        <div>
          <label className={labelClass}>PIN Code <span className="text-[#e2916d]">*</span></label>
          <input
            type="text"
            name="pincode"
            value={formData.pincode}
            onChange={handleInputChange}
            pattern="[0-9]{6}"
            className={inputClass}
            placeholder="000000"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-[18px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div>
          <label className={labelClass}>Admin Email <span className="text-[#e2916d]">*</span></label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="email"
              name="adminEmail"
              value={formData.adminEmail}
              onChange={handleInputChange}
              className={inputWithIconClass}
              placeholder="admin@school.com"
              required
            />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-[#e2916d] mt-1.5">
            <Mail className="w-3 h-3 flex-shrink-0" />
            This email will be used to login to your admin account
          </p>
        </div>

        <div>
          <label className={labelClass}>Admin Mobile <span className="text-[#e2916d]">*</span></label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="tel"
              name="adminMobile"
              value={formData.adminMobile}
              onChange={handleInputChange}
              pattern="[0-9]{10}"
              className={inputWithIconClass}
              placeholder="9876543210"
              required
            />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-[#e2916d] mt-1.5">
            <Phone className="w-3 h-3 flex-shrink-0" />
            Used for account verification
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div>
          <label className={labelClass}>Admin Password <span className="text-[#e2916d]">*</span></label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="password"
              name="adminPassword"
              value={formData.adminPassword}
              onChange={handleInputChange}
              className={inputWithIconClass}
              placeholder="Enter password"
              minLength={6}
              required
            />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-[#e2916d] mt-1.5">
            <Lock className="w-3 h-3 flex-shrink-0" />
            Used to login to the admin portal
          </p>
        </div>

        <div>
          <label className={labelClass}>Confirm Password <span className="text-[#e2916d]">*</span></label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={inputWithIconClass}
              placeholder="Confirm password"
              minLength={6}
              required
            />
          </div>
          {formData.confirmPassword && formData.adminPassword !== formData.confirmPassword && (
            <p className="flex items-center gap-1.5 text-xs text-[#f38b83] mt-1.5">
              <X className="w-3 h-3 flex-shrink-0" />
              Passwords do not match
            </p>
          )}
          {formData.confirmPassword && formData.adminPassword === formData.confirmPassword && (
            <p className="flex items-center gap-1.5 text-xs text-[#4ade80] mt-1.5">
              <Check className="w-3 h-3 flex-shrink-0" />
              Passwords match
            </p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Principal Name <span className="text-[#e2916d]">*</span></label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
          <input
            type="text"
            name="principalName"
            value={formData.principalName}
            onChange={handleInputChange}
            className={inputWithIconClass}
            placeholder="Enter principal name"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div>
          <label className={labelClass}>Principal Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="email"
              name="principalEmail"
              value={formData.principalEmail}
              onChange={handleInputChange}
              className={inputWithIconClass}
              placeholder="principal@school.com"
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Principal Mobile</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="tel"
              name="principalMobile"
              value={formData.principalMobile}
              onChange={handleInputChange}
              pattern="[0-9]{10}"
              className={inputWithIconClass}
              placeholder="9876543210"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-[18px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div>
          <label className={labelClass}>School Type <span className="text-[#e2916d]">*</span></label>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e] pointer-events-none" />
            <select
              name="schoolType"
              value={formData.schoolType}
              onChange={handleInputChange}
              className={inputWithIconClass}
              required
            >
              <option value="">Select school type</option>
              <option value="government">Government</option>
              <option value="private">Private</option>
              <option value="aided">Government Aided</option>
              <option value="international">International</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Established Year <span className="text-[#e2916d]">*</span></label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="number"
              name="establishedYear"
              value={formData.establishedYear}
              onChange={handleInputChange}
              min="1900"
              max={new Date().getFullYear()}
              className={inputWithIconClass}
              placeholder="2020"
              required
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        <div>
          <label className={labelClass}>Total Students (Approx.)</label>
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e] pointer-events-none" />
            <select
              name="totalStudents"
              value={formData.totalStudents}
              onChange={handleInputChange}
              className={inputWithIconClass}
            >
              <option value="">Select range</option>
              <option value="0-100">0-100</option>
              <option value="101-500">101-500</option>
              <option value="501-1000">501-1000</option>
              <option value="1001-2000">1001-2000</option>
              <option value="2000+">2000+</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Website</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className={inputWithIconClass}
              placeholder="https://www.school.com"
            />
          </div>
        </div>
      </div>

      <div>
        <label className={labelClass}>Affiliation/Board <span className="text-[#e2916d]">*</span></label>
        <select
          name="affiliation"
          value={formData.affiliation}
          onChange={handleInputChange}
          className={inputClass}
          required
        >
          <option value="">Select affiliation</option>
          <option value="CBSE">CBSE</option>
          <option value="ICSE">ICSE</option>
          <option value="State Board">State Board</option>
          <option value="IB">International Baccalaureate (IB)</option>
          <option value="Cambridge">Cambridge</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <label className={labelClass}>School Logo</label>
        <div className="border-2 border-dashed border-[#3a3122] rounded-xl p-6 hover:border-[#e2916d] transition-colors">
          <input
            type="file"
            id="logo-upload"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <label
            htmlFor="logo-upload"
            className="cursor-pointer flex flex-col items-center justify-center"
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="School Logo Preview"
                className="w-24 h-24 object-contain mb-3 rounded-lg"
              />
            ) : (
              <Upload className="w-9 h-9 text-[#8a7f6e] mb-3" />
            )}
            <p className="text-[#b9ad9b] text-sm text-center">
              {logoPreview ? 'Click to change logo' : 'Click to upload school logo'}
            </p>
            <p className="text-xs text-[#8a7f6e] mt-1.5">PNG, JPG up to 5MB</p>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#17140f] text-[#f3ede2]">
      {/* Header */}
      <header className="border-b border-[#3a3122]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 bg-[#e2916d] rounded-[10px] flex items-center justify-center flex-shrink-0">
              <School className="w-[18px] h-[18px] text-[#241408]" />
            </div>
            <span className="text-base font-bold">Kaksha360</span>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1 text-sm font-semibold text-[#b9ad9b] hover:text-[#e2916d] transition-colors"
          >
            Already registered? Log in
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-[1080px] mx-auto px-6 pt-14 pb-20 grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-14">
        {/* Left panel */}
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#e2916d] mb-2.5">School Registration</p>
          <h1 className="text-[32px] font-bold leading-[1.18] tracking-tight mb-3.5">
            Join Kaksha360 in three steps.
          </h1>
          <p className="text-[15px] text-[#b9ad9b] leading-relaxed max-w-[38ch] mb-8">
            Tell us about your school, set up your admin login, and add a few final details. Most schools finish in under five minutes.
          </p>

          <div className="flex flex-col gap-2.5 mb-8">
            {steps.map((step, index) => {
              const stepNum = index + 1;
              const isActive = currentStep === stepNum;
              const isDone = currentStep > stepNum;
              return (
                <div
                  key={step.title}
                  className={`flex items-center gap-3 p-3.5 rounded-[14px] border ${
                    isActive ? 'bg-[#211c15] border-[#3a3122]' : 'border-transparent'
                  }`}
                >
                  <span
                    className={`w-[30px] h-[30px] rounded-[9px] flex-shrink-0 flex items-center justify-center text-[13px] font-bold ${
                      isActive
                        ? 'bg-[#e2916d] text-[#241408]'
                        : isDone
                        ? 'bg-[#173322] text-[#4ade80]'
                        : 'bg-[#28221a] text-[#8a7f6e]'
                    }`}
                  >
                    {isDone ? <Check className="w-3.5 h-3.5" /> : stepNum}
                  </span>
                  <div>
                    <div className={`text-[13.5px] font-semibold ${isActive ? 'text-[#f3ede2]' : 'text-[#b9ad9b]'}`}>{step.title}</div>
                    <div className="text-[11.5px] text-[#8a7f6e]">{step.subtitle}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-2.5 text-[13px] text-[#b9ad9b] border-t border-[#3a3122] pt-6">
            <span className="flex items-center gap-2"><Check className="w-[15px] h-[15px] text-[#4ade80] flex-shrink-0" />No setup fees</span>
            <span className="flex items-center gap-2"><Check className="w-[15px] h-[15px] text-[#4ade80] flex-shrink-0" />Reviewed within 2–3 business days</span>
            <span className="flex items-center gap-2"><Check className="w-[15px] h-[15px] text-[#4ade80] flex-shrink-0" />Cloud-hosted &amp; secure</span>
          </div>
        </div>

        {/* Right panel: form card */}
        <div>
          <div className="bg-[#211c15] border border-[#3a3122] rounded-[22px] p-6 sm:p-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#e2916d]">Step {currentStep} of 3</span>
              <span className="text-xs font-semibold text-[#8a7f6e]">{steps[currentStep - 1].title}</span>
            </div>
            <div className="h-1 bg-[#332b20] rounded-full overflow-hidden mb-7">
              <div
                className="h-full bg-[#e2916d] rounded-full transition-all duration-300 ease-snappy-out"
                style={{ width: `${(currentStep / 3) * 100}%` }}
              />
            </div>

            {error && (
              <div className="mb-5 p-3 bg-[#341a17] border border-[#f38b83]/30 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#f38b83] flex-shrink-0" />
                <span className="text-[#f38b83] text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-7 pt-6 border-t border-[#3a3122]">
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-[#b9ad9b] hover:text-[#f3ede2] transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      Previous
                    </button>
                  )}
                </div>

                <div>
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="inline-flex items-center gap-1.5 bg-[#e2916d] hover:-translate-y-0.5 transition-transform text-[#241408] font-semibold text-sm py-3 px-6 rounded-xl"
                    >
                      Next Step
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 bg-[#e2916d] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-transform text-[#241408] font-semibold text-sm py-3 px-6 rounded-xl"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-[#241408] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span>Submit Registration</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          <p className="text-center mt-5 text-[13px] text-[#b9ad9b]">
            Trouble registering? <a href="mailto:support@kaksha360.com" className="text-[#e2916d] font-semibold hover:underline">Contact support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SchoolRegistration;
